// wishlist powered by swym.
// See https://swym.it/help/developer-documentation/ and https://developers.swym.it/v2.0/docs/simple-wishlist
import { createState } from "../helpers/state-subject";
import { fetchAdditionalProductVariantInfo } from "../helpers/cart.helper";
import { emitShopifyEvent } from "./analytics.service";
import { logInstanceId } from "../helpers/state";
logInstanceId("wishlist.service");
const WISHLIST_EVENT_TYPE = 4;
const sflResponseStore = createState(null);
// Enriched cache + multicast: enrich the raw response once and notify all subscribers.
// New subscribers receive the last enriched result immediately if one is already available.
let latestEnrichedData = null;
let fetchId = 0;
const sflSubscribers = new Set();
async function onRawResponseSet(response) {
    if (!response) {
        return;
    }
    const currentId = ++fetchId;
    const enriched = await addAdditionalProductVariantInfo(response);
    if (currentId !== fetchId || !enriched) {
        // a newer fetchSaveForLaterList call has superseded this one
        return;
    }
    latestEnrichedData = enriched;
    for (const fn of [...sflSubscribers]) {
        fn(enriched);
    }
}
sflResponseStore.subscribe(onRawResponseSet);
export const sflData = {
    subscribe(listener) {
        sflSubscribers.add(listener);
        if (latestEnrichedData) {
            listener(latestEnrichedData);
        }
        return () => sflSubscribers.delete(listener);
    },
};
function toSwymRejectionError(message, error) {
    if (error instanceof Error) {
        return error;
    }
    return new Error(message, error !== undefined ? { cause: error } : undefined);
}
export function openWishlist() {
    getGlobalWishlistObject().push((swat) => {
        swat.ui.open();
    });
}
export function renderWishlistCounter() {
    getGlobalWishlistObject().push((swat) => {
        if (swat.renderWishlistCount) {
            const counterEls = document.querySelectorAll(".js-wishlist-counter");
            counterEls.forEach((el) => {
                swat.renderWishlistCount(el);
            });
        }
    });
}
export function whenWishlistLoaded() {
    return new Promise((resolve) => {
        getGlobalWishlistObject().push(() => {
            console.log("wishlist loaded");
            resolve();
        });
    });
}
export function isProductInWishlist(productId) {
    return new Promise((resolve) => {
        getGlobalWishlistObject().push((swat) => {
            swat.fetchWrtEventTypeET((products) => {
                const product = products && products.find((p) => p.empi === productId);
                const isFound = !!product;
                console.log("wishlist product found?", isFound);
                resolve(isFound);
            }, WISHLIST_EVENT_TYPE);
        });
    });
}
export function addProductToWishlist(productData) {
    const payload = productData || window.SwymPageData;
    return new Promise((resolve, reject) => {
        if (payload) {
            payload.et = WISHLIST_EVENT_TYPE;
            getGlobalWishlistObject().push((swat) => {
                swat.addToWishList(payload, (result) => {
                    console.log("product added to wishlist", result);
                    resolve();
                });
            });
        }
        else {
            console.error("Unable to add to wishlist; payload is undefined.");
            reject(new Error("Unable to add to wishlist; payload is undefined."));
        }
    });
}
export function removeProductFromWishlist(productId) {
    return new Promise((resolve, reject) => {
        getGlobalWishlistObject().push((swat) => {
            swat.fetch((products) => {
                if (products) {
                    const productToRemove = products.find(p => p.empi === productId);
                    if (productToRemove) {
                        getGlobalWishlistObject().push((swat) => {
                            swat.removeFromWishList(productToRemove, () => {
                                resolve();
                            });
                        });
                    }
                    else {
                        console.log(`Unable to remove from wishlist; ${productId} doesn't exist in wishlisted products`);
                        reject(new Error(`Unable to remove from wishlist; product ${productId} is not wishlisted.`));
                    }
                }
            });
        });
    });
}
export function refreshWishlistIcons(selector) {
    getGlobalWishlistObject().push((swat) => {
        swat.initializeActionButtons(selector);
    });
}
export function initSaveForLater() {
    return new Promise((resolve, reject) => {
        getGlobalWishlistObject().push((swat) => {
            swat.SaveForLater.init((response) => {
                console.log("swym save for later init success");
                window._saveforLaterListId = response.list.lid;
                fetchSaveForLaterList().catch((error) => {
                    console.error("swym: error fetching save for later list after init", error);
                });
                resolve(response.list.lid);
            }, (error) => {
                console.error("swym: error creating save for later list", error);
                reject(toSwymRejectionError("swym: error creating save for later list", error));
            });
        });
    });
}
export function saveProductsForLater(products) {
    return new Promise((resolve, reject) => {
        getGlobalWishlistObject().push((swat) => {
            swat.SaveForLater.add(window._saveforLaterListId, products, () => {
                fetchSaveForLaterList();
                resolve();
            }, (error) => {
                console.error("swym: error saving products to save for later list", error);
                reject(toSwymRejectionError("swym: error saving products to save for later list", error));
            });
        });
    });
}
export function fetchSaveForLaterList() {
    return new Promise((resolve, reject) => {
        getGlobalWishlistObject().push((swat) => {
            swat.SaveForLater.fetch(window._saveforLaterListId, (response) => {
                sflResponseStore.set(response);
                resolve(response);
            }, (error) => {
                console.error("swym: error fetching save for later list", error);
                reject(toSwymRejectionError("swym: error fetching save for later list", error));
            });
        });
    });
}
export function removeSaveForLaterProducts(products) {
    return new Promise((resolve, reject) => {
        getGlobalWishlistObject().push((swat) => {
            swat.SaveForLater.remove(window._saveforLaterListId, products, () => {
                fetchSaveForLaterList();
                resolve();
            }, (error) => {
                console.error("swym: error removing products from save for later list", error);
                reject(toSwymRejectionError("swym: error removing products from save for later list", error));
            });
        });
    });
}
export function initWishlistEventListeners() {
    whenWishlistLoaded().then(() => {
        window.SwymCallbacks.push((swat) => {
            console.log("swym wishlist event listeners initialized");
            swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, (e) => {
                const result = e.detail.d;
                emitShopifyEvent("untuckit:product_added_to_wishlist", { sku: result.sku });
            });
            // "fix" for the added product's title being displayed as the page title; code provided by swym support
            swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, function (data) {
                const eventData = data.detail.d;
                const empi = eventData.empi;
                const du = eventData.du;
                swat.getProductDetails({ empi, du }, (product) => {
                    const productTitle = product?.title;
                    setTimeout(() => {
                        const titleEl = document.querySelector('.swym-notification-wishlist .swym-emphasis');
                        if (titleEl) {
                            titleEl.textContent = productTitle;
                        }
                    }, 40);
                });
            });
        });
    });
}
function getGlobalWishlistObject() {
    return window.SwymCallbacks = window.SwymCallbacks || [];
}
async function addAdditionalProductVariantInfo(response) {
    const variantIds = response.items.map(p => p.epi);
    try {
        const addedProductData = await fetchAdditionalProductVariantInfo(variantIds, { badgePrefix: "badge-pdp:" });
        const val = response;
        val.items = response.items.map(item => {
            const extraData = addedProductData[item.epi];
            return extraData ? { ...item, ...extraData } : item;
        });
        return val;
    }
    catch (err) {
        console.error(err);
    }
}
